import {SimplePresentation} from "./SimplePresentationRunner";
import {Possessed} from "../../Possessed";
import {PossessedSymbol, PossessedSymbolStates} from "../../reels/PossessedSymbol";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {gsap} from "gsap";
import {IWinRatioConfig, PossessedWinConfig} from '../../config/PossessedWinConfig';
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {ArrayUtils} from "../../utils/ArrayUtils";


export class WinPresentation extends SimplePresentation {

    private isSkipping = false;

    constructor() {
        super("WinPresentation");
    }

    public override shouldRun(): boolean {
        return Possessed.model.gameData.thisSpinWinning > 0;
    }

    protected skip() {
        this.isSkipping = true;
        this.tl.progress(1);
        this.isSkipping = false;

        const isMidWin = PossessedWinConfig.getWinConfig(Possessed.model.getThisSpinWinRatio()).winLevel >= 2;

        const baseSound = isMidWin ? "winSweepMid0" : "winSweepTiny0";

        let firstSound = true;
        for(let i = 1; i <= 5; i++) {
            if(Possessed.soundPlayer.isSoundPlaying(baseSound + i)) {
                if(firstSound) {
                    firstSound = false;
                } else {
                    Possessed.soundPlayer.stopSound(baseSound + i);
                }
            }
        }
        if(firstSound == true) {
            Possessed.soundPlayer.playSound(baseSound + "1");
        }
    }

    buildTimeline() {
        const winConfig = PossessedWinConfig.getWinConfig(Possessed.model.getThisSpinWinRatio());

        // Padding before win
        this.tl.add(() => {}, '>0.3');

        if(Possessed.model.gameData.brokeTheBank && Possessed.model.getWonWays() > 0) {
            this.tl.add(this.showWinGroups());
        } else if(winConfig.bigWinLevel > 0 && !Possessed.model.gameData.brokeTheBank) {
            this.controller.setSkipCallback();
            this.bigWin();
        } else if(!Possessed.model.gameData.brokeTheBank) {
            this.tl.add(this.regularWin());
        }

    }

    private showWinGroups():gsap.core.Timeline {
        const tl = gsap.timeline();
        // Dim hotZone/cross fire animation
        tl.add(Possessed.reelView.reelCross.dimActiveHotZones());


        const winningSymbols = Possessed.reelView.getAllVisibleSymbols()
            .filter(s => Possessed.model.getWinningPositions().some(wp => wp.reelID == s.reelID && wp.reelPosition == s.reelPosition));

        const nonWinningSymbols = Possessed.reelView.getAllVisibleSymbols().filter(s => !winningSymbols.some(ws => ws == s));

        const winRatio = Possessed.model.getThisSpinWinRatio();
        const winConf = PossessedWinConfig.getWinConfig(winRatio);
        const wonWays = Possessed.model.getWonWays();

        // Darken non winning
        nonWinningSymbols.forEach(sym => {
            tl.add(sym.playNoWin(), "<");
        })

        // Play symbol wins
        const symbolsWinTL = gsap.timeline();
        winningSymbols.forEach(sym  => {
            symbolsWinTL.add(() => {
                sym.moveToWinLayer();
            })
            symbolsWinTL.add(sym.playWin(winRatio), sym.reelID * 0.17);
        });
        for(let reelID = 0; reelID <= Math.max(...winningSymbols.map(sym => sym.reelID)); reelID++) {
            symbolsWinTL.add(() => {
                let sweepSound = winConf.winLevel >= 2 ? "winSweepMid0" : "winSweepTiny0";
                sweepSound += Math.round((Math.random() * 4)) + 1;
                Possessed.soundPlayer.playSoundWithDavidPanning(sweepSound, reelID, 1);
            }, reelID * 0.17)
        }

        tl.add(symbolsWinTL);

        // Padding
        tl.add(()=>{}, ">0.5");

        return tl;
    }

    private regularWin(updateKeypad:boolean = true):gsap.core.Timeline {
        const tl = gsap.timeline();

        // Dim hotZone/cross fire animation
        tl.add(Possessed.reelView.reelCross.dimActiveHotZones());

        const visibleSymbols = Possessed.reelView.getAllVisibleSymbols();
        const winningSymbols = visibleSymbols.filter(s => Possessed.model.getWinningPositions().some(wp => wp.reelID == s.reelID && wp.reelPosition == s.reelPosition));

        const nonWinningSymbols = Possessed.reelView.getAllVisibleSymbols().filter(s => !winningSymbols.some(ws => ws == s));

        const winRatio = Possessed.model.getThisSpinWinRatio();
        const winConf = PossessedWinConfig.getWinConfig(winRatio);
        const wonWays = Possessed.model.getWonWays(); 

        // Darken non winning
        nonWinningSymbols.forEach(sym => {
            tl.add(sym.playNoWin(), "<");
        })

        // Play symbol wins
        const symbolsWinTL = gsap.timeline();
        winningSymbols.forEach(sym  => {
            symbolsWinTL.add(() => {
                sym.moveToWinLayer();
            })
            symbolsWinTL.add(sym.playWin(winRatio), sym.reelID * 0.17);
        });


        for(let reelID = 0; reelID <= Math.max(...winningSymbols.map(sym => sym.reelID)); reelID++) {
            symbolsWinTL.add(() => {
                if(!this.isSkipping) {
                    let sweepSound = winConf.winLevel >= 2 ? "winSweepMid0" : "winSweepTiny0";
                    sweepSound += Math.round((Math.random() * 4)) + 1;
                    Possessed.soundPlayer.playSoundWithDavidPanning(sweepSound, reelID, 1);
                }
            }, reelID * 0.17)
        }
        tl.add(symbolsWinTL);

        // Win counter
        if(Possessed.model.isWinBelowStake() == false) {
            const winConf = PossessedWinConfig.getWinConfig(winRatio);

            if(winConf.duration == 0) {
                tl.add(Possessed.winCounter.show(Possessed.model.gameData.thisSpinWinning, wonWays), ">-0.3");
            } else {
                tl.add(Possessed.winCounter.countUp(
                    0,
                    Possessed.model.gameData.thisSpinWinning,
                    winConf.duration,
                    wonWays
                ), ">-0.3");

                tl.add(()=> {Possessed.soundPlayer.playSound("countup")}, "<");
                tl.add(() => {Possessed.soundPlayer.stopSound("countup")}, `>${winConf.duration}`);

                const clamp = (value:number, min:number, max:number) => {
                    return Math.max(Math.min(value, max), min);
                }

                const bounceScaleFactor = clamp(winRatio*0.4, 1.07, 1.25);
                const bouncePosOffset = clamp(winRatio*5, 5, 30);
                tl.add(Possessed.winCounter.postCountUpBounce(bounceScaleFactor, bouncePosOffset));
            }

            //tl.add(Possessed.winCounter.show(Possessed.model.gameData.thisSpinWinning, wonWays));

            const stopSounds = [
                "countupEndsTiny",
                "countupEndsSmall",
                "countupEndsMedium",
                "countupEndsLarge",
            ];
            tl.add(()=> {Possessed.soundPlayer.playSound(`${stopSounds[winConf.winLevel]}`)}, "<");

            // Idle WinCounter
            tl.add(()=>{}, ">1");
            tl.add(Possessed.winCounter.hide());
        }

        if(updateKeypad) {
            const totalWin = Possessed.model.data.mode != GameMode.NORMAL || Possessed.model.data.nextMode != GameMode.NORMAL;
            tl.add(() => SlotGame.keypadPlugin.setWin(Possessed.model.data.accumulatedRoundWin, totalWin, Possessed.model.isWinBelowStake()));
        }

        // Move wins to normal layer
        winningSymbols.forEach(sym => {
            tl.add(sym.setNormal(), "<");
            tl.add(() => {sym.moveToNormalLayer()}, "<");
        })
        
        // Remove hotZone dim
        tl.add(Possessed.reelView.reelCross.removeHotzoneDim());



        // Set all symbol to normal state
        tl.add(Possessed.reelView.getAllVisibleSymbols().map(sym => sym.setNormal()), "<");

        return tl;
    }
    private bigWin():gsap.core.Timeline {
        const winningSymbols:PossessedSymbol[] = Possessed.model.getWinningPositions().map(pos => Possessed.reelView.getSymbolAt(pos.reelID, pos.reelPosition));
        const nonWinningSymbols:PossessedSymbol[] = Possessed.reelView.getAllVisibleSymbols().filter(s => !winningSymbols.some(ws => ws == s));
        
        //this.tl.add(() => {
        //    this.controller.setSkipCallback(() => {
        //        this.tl.seek("showWays", false);
        //    })
        //   //Possessed.soundPlayer.fadeOutMusic();
        //})

        // Play symbol win animations and darken non winning
        nonWinningSymbols.forEach(sym => {
            this.tl.add(sym.playNoWin(), "<");
        })

        const symbolWinTL = gsap.timeline();
        winningSymbols.forEach(sym => {
            symbolWinTL.add(() => {sym.moveToWinLayer()}, 0);
            symbolWinTL.add(sym.playWin(Possessed.model.getThisSpinWinRatio()), sym.reelID * 0.17);
        })

        const winSpeepSounds = ArrayUtils.shuffle(["winSweepBig01", "winSweepBig02", "winSweepBig03", "winSweepBig04", "winSweepBig05"]);
        for(let reelID = 0; reelID <= Math.max(...winningSymbols.map(sym => sym.reelID)); reelID++) {
            symbolWinTL.add(() => {
                const sweepSound = winSpeepSounds[reelID];
                Possessed.soundPlayer.playSoundWithDavidPanning(sweepSound, reelID, 1);
            }, reelID * 0.17)
        }
        this.tl.add(symbolWinTL);

        const winLevels = PossessedWinConfig.getBigWinLevels(Possessed.model.getThisSpinWinRatio());
        let accumulatedWinAmount = 0;
        let winWays:number = Possessed.model.getWonWays();

        this.tl.add(()=> {
            Possessed.soundPlayer.fadeOutMusic();
            Possessed.soundPlayer.playSound("bigwinIntro", 1);
        }, winWays > 25 ? ">" : "<");


        this.tl.addLabel("showWays");
        if(winWays > 25 && Possessed.model.isWinBelowStake() == false) {
            this.tl.add(() => {
                this.controller.setSkipCallback(() => {
                    this.tl.seek("bigWinsStart", false);
                })
            })
            this.tl.add(Possessed.winCounter.playPreBigWinWaysHighlight(winWays));
        }
        this.tl.addLabel("bigWinsStart");
        this.tl.add(()=>{
            this.controller.setSkipCallback();
        })
        this.tl.add(()=> {Possessed.soundPlayer.stopSound("bigwinIntro")}, "<");

        winLevels.forEach((level, index, levels) => {
            const isLastLevel = index == winLevels.length-1;
            const isFirst = index == 0;

            this.tl.add(() => {
                this.controller.setSkipCallback(() => {
                    this.tl.seek(`winEnd${level.bigWinLevel}`, false);
                })
            })
            const nextLevel:IWinRatioConfig = isLastLevel ? level : levels[index+1];

            const countFrom = isFirst ? 0 : Possessed.model.data.playedBetValue * level.ratio;
            let countTo = isLastLevel ? Possessed.model.data.thisSpinWinning : Possessed.model.data.playedBetValue * Math.min(Possessed.model.getThisSpinWinRatio(), nextLevel.ratio);
            this.tl.add(Possessed.winCounter.playBigWinCountUp(level, countFrom, countTo));
            this.tl.add(() => {
                Possessed.soundPlayer.playSoundNoOverlap("bigwin" + level.bigWinLevel, 1, 0, 0, 0.2);
            }, "<")

            this.tl.addLabel(`winEnd${level.bigWinLevel}`);

            // https://github.com/nolimitcity/possessed/issues/215
            this.tl.add(() => { this.controller.setSkipCallback(); });

            this.tl.add(() => {
                Possessed.soundPlayer.stopSound("bigwin" + level.bigWinLevel, 10);
            })
        })


        const totalWin = Possessed.model.data.mode != GameMode.NORMAL || Possessed.model.data.nextMode != GameMode.NORMAL;
        this.tl.add(() => {SlotGame.keypadPlugin.setWin(Possessed.model.data.accumulatedRoundWin, totalWin, Possessed.model.isWinBelowStake())});

        this.tl.add(() => {
            Possessed.soundPlayer.playSoundNoOverlap("bigwinEnd");
            Possessed.soundPlayer.fadeInMusic();
        });

        // Padding before hiding win view
        this.tl.add(()=>{}, ">0.7");
        this.tl.add(Possessed.winCounter.hide());


        [...winningSymbols, ...nonWinningSymbols].forEach(sym => {
            this.tl.add(()=> {sym.moveToNormalLayer()}, "<");
            this.tl.add(sym.setNormal(), "<");
        })

        return this.tl;
    }

    public static playWinToggle():gsap.core.Timeline {
        const tl = gsap.timeline();

        const wins = Possessed.model.wins;
        let totalWays = 0;

        // Highlight winlines one by one
        wins.forEach((win, index) => {
            const winLineTL = gsap.timeline();
            const winRatio:number = win.winLineData.amount / Possessed.model.data.playedBetValue;

            const winningSymbols = win.winningPositions.map(pos => {
                return Possessed.reelView.getSymbolAt(pos.reelID, pos.reelPosition);
            })
            const nonWinningSymbols = Possessed.reelView.getAllVisibleSymbols()
            .filter(sym => sym.getState() != PossessedSymbolStates.NO_WIN)
            .filter(sym => !winningSymbols.some(s => s == sym));
            
            // Remove darken on winning symbols
            winningSymbols.filter(s => s.getState() == PossessedSymbolStates.NO_WIN).forEach(sym => {
                winLineTL.add(sym.setNormal(), "<");
            })

            // Darken non winning
            nonWinningSymbols.forEach(sym => {
                winLineTL.add(sym.playNoWin(), "<");
            })

            // Play symbol wins
            const symbolsWinTL = gsap.timeline();
            winningSymbols.forEach(sym  => {
                symbolsWinTL.add(() => {
                    sym.moveToWinLayer();
                })
                symbolsWinTL.add(sym.playWinToggle(), sym.reelID * 0.17);
            });
            winLineTL.add(symbolsWinTL);

            // Win counter
            if(Possessed.model.isWinBelowStake() == false) {
                const winConf = PossessedWinConfig.getWinConfig(winRatio);
                winLineTL.add(Possessed.winCounter.show(win.winLineData.multipliedAmount, win.winLineData.numberOfCombinations), ">-1.5");
            }
            
            winLineTL.add(()=>{}, ">0.6");

            // Move wins to normal layer
            winningSymbols.forEach((sym, index) => {
                winLineTL.add(sym.setNormal(), index == 0 ? ">" : "<");
                winLineTL.add(() => {sym.moveToNormalLayer()}, "<");
            })

            // Hide Counter
            winLineTL.add(Possessed.winCounter.hide());
            tl.add(winLineTL);
            totalWays += win.winLineData.numberOfCombinations;
        });
        
        
        // Higlight all winlines combined if we have more than one winline
        if(wins.length > 1) {
            tl.add(()=>{}, ">0.1");
            const winRatio = Possessed.model.getThisSpinWinRatio();
             // Set all symbol to normal state
            tl.add( () => {
            Possessed.reelView.getAllVisibleSymbols().forEach(sym => {
                sym.moveToNormalLayer();
            })
            }, "<")
            tl.add(Possessed.reelView.getAllVisibleSymbols().map(sym => sym.setNormal()), "<");

            const allWinningSymbols = Possessed.reelView.getAllVisibleSymbols()
                .filter(sym => Possessed.model.getWinningPositions().some(pos => pos.reelID == sym.reelID && pos.reelPosition == sym.reelPosition));
            const nonWinningSymbols = Possessed.reelView.getAllVisibleSymbols()
                .filter(sym => !allWinningSymbols.some(s => s == sym))
    
            
            nonWinningSymbols.forEach((sym, index) => {
                tl.add(sym.playNoWin(), index == 0 ? ">" : "<");
            })
    
            const symbolWinTL = gsap.timeline();
            allWinningSymbols.forEach(sym => {
                symbolWinTL.add(() => {
                    sym.moveToWinLayer();
                }, "<")
                symbolWinTL.add(sym.playWinToggle(), sym.reelID * 0.17);
            })
            tl.add(symbolWinTL);
            
            if(Possessed.model.isWinBelowStake() == false) {
                tl.add(Possessed.winCounter.show(Possessed.model.data.thisSpinWinning, totalWays), ">-1.5");
            }
            tl.add(()=> {}, ">0.7");
            
            // Set all symbol to normal state
            tl.add( () => {
                Possessed.reelView.getAllVisibleSymbols().forEach(sym => {
                    sym.moveToNormalLayer();
                })
            })
            tl.add(Possessed.reelView.getAllVisibleSymbols().map(sym => sym.setNormal()), "<");
    
            tl.add(Possessed.winCounter.hide(), "<");
        } else {
            tl.add(()=>{}, ">")
            tl.add(Possessed.reelView.getAllVisibleSymbols().map(s => s.setNormal()), "<");
        }

        return tl;
    } 
}